import type {
  TriggerEvent,
  TriggerOccurrence,
  TriggerObservation,
  TriggerPhase,
  TriggerStateSnapshot,
  TriggerStateRow,
  TriggerTransition
} from './types'

function record(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

function optionalNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

function phase(value: unknown): TriggerPhase | undefined {
  return value === 'inactive' ||
    value === 'pending_on' ||
    value === 'active' ||
    value === 'pending_off'
    ? value
    : undefined
}

function observation(value: unknown): TriggerObservation | undefined {
  if (value === 'active') return 'true'
  if (value === 'inactive') return 'false'
  return value === 'true' || value === 'false' || value === 'unknown' || value === 'error'
    ? value
    : undefined
}

function normalizeRow(value: unknown): TriggerStateRow | undefined {
  const row = record(value)
  const triggerId = optionalString(row?.trigger_id)
  const triggerPhase = phase(row?.phase)
  if (!row || !triggerId || !triggerPhase) return undefined
  const pendingSeconds = optionalNumber(row.pending_seconds)
  const pendingElapsedMs =
    optionalNumber(row.pending_elapsed_ms) ??
    (pendingSeconds === undefined ? undefined : pendingSeconds * 1000)
  return {
    trigger_id: triggerId,
    phase: triggerPhase,
    last_observation: observation(row.last_observation),
    pending_elapsed_ms: pendingElapsedMs,
    on_delay_seconds: optionalNumber(row.on_delay_seconds),
    off_delay_seconds: optionalNumber(row.off_delay_seconds),
    activated_at: row.activated_at === null ? null : optionalString(row.activated_at),
    last_cleared_at: row.last_cleared_at === null ? null : optionalString(row.last_cleared_at),
    last_evaluated_at:
      row.last_evaluated_at === null ? null : optionalString(row.last_evaluated_at),
    stale: typeof row.stale === 'boolean' ? row.stale : undefined,
    quality: optionalString(row.quality),
    is_good: typeof row.is_good === 'boolean' ? row.is_good : undefined,
    reason: optionalString(row.reason)
  }
}

function triggerEvent(value: unknown): TriggerEvent | undefined {
  return value === 'activated' || value === 'cleared' ? value : undefined
}

function normalizeOccurrence(value: unknown): TriggerOccurrence | undefined {
  const row = record(value)
  const id = optionalString(row?.id)
  const triggerId = optionalString(row?.trigger_id)
  const openedAt = optionalString(row?.opened_at)
  if (!row || !id || !triggerId || !openedAt) return undefined
  return {
    id,
    trigger_id: triggerId,
    opened_at: openedAt,
    cleared_at: row.cleared_at === null ? null : optionalString(row.cleared_at),
    baseline: typeof row.baseline === 'boolean' ? row.baseline : undefined,
    acknowledged_at: row.acknowledged_at === null ? null : optionalString(row.acknowledged_at),
    acknowledged_by: row.acknowledged_by === null ? null : optionalString(row.acknowledged_by),
    suppressed_at: row.suppressed_at === null ? null : optionalString(row.suppressed_at),
    suppressed_by: row.suppressed_by === null ? null : optionalString(row.suppressed_by),
    suppress_until_clear:
      typeof row.suppress_until_clear === 'boolean' ? row.suppress_until_clear : undefined,
    unsuppressed_at: row.unsuppressed_at === null ? null : optionalString(row.unsuppressed_at),
    unsuppressed_by: row.unsuppressed_by === null ? null : optionalString(row.unsuppressed_by)
  }
}

function normalizeTransition(value: unknown): TriggerTransition | undefined {
  const row = record(value)
  const id = optionalString(row?.id)
  const triggerId = optionalString(row?.trigger_id)
  const occurrenceId = optionalString(row?.occurrence_id)
  const event = triggerEvent(row?.event)
  const transitionedAt = optionalString(row?.transitioned_at)
  if (!row || !id || !triggerId || !occurrenceId || !event || !transitionedAt) return undefined
  const actionStatus =
    row.action_status === 'pending' ||
    row.action_status === 'handled' ||
    row.action_status === 'suppressed' ||
    row.action_status === 'failed'
      ? row.action_status
      : undefined
  return {
    id,
    trigger_id: triggerId,
    occurrence_id: occurrenceId,
    event,
    transitioned_at: transitionedAt,
    action_status: actionStatus,
    action_reason: optionalString(row.action_reason),
    handled_at: row.handled_at === null ? null : optionalString(row.handled_at)
  }
}

/**
 * Normalize both the finalized response (`trigger_states`) and transitional
 * response spellings used during rolling deployment.
 */
export function normalizeTriggerStateSnapshot(
  raw: unknown,
  fallbackOwnerId: string
): TriggerStateSnapshot {
  const body = record(raw)
  const candidateRows = Array.isArray(raw)
    ? raw
    : Array.isArray(body?.trigger_states)
      ? body.trigger_states
      : Array.isArray(body?.states)
        ? body.states
        : Array.isArray(body?.triggers)
          ? body.triggers
          : []
  const ownerId =
    optionalString(body?.custom_agent_config_id) ??
    optionalString(body?.notification_rule_id) ??
    optionalString(body?.owner_id) ??
    fallbackOwnerId

  return {
    ownerId,
    evaluatedAt: optionalString(body?.evaluated_at) ?? optionalString(body?.last_evaluated_at),
    triggers: candidateRows
      .map(normalizeRow)
      .filter((row): row is TriggerStateRow => row !== undefined),
    occurrences: (Array.isArray(body?.occurrences) ? body.occurrences : [])
      .map(normalizeOccurrence)
      .filter((row): row is TriggerOccurrence => row !== undefined),
    transitions: (Array.isArray(body?.transitions) ? body.transitions : [])
      .map(normalizeTransition)
      .filter((row): row is TriggerTransition => row !== undefined)
  }
}
